import { CommonModule } from '@angular/common';
import { Component, OnInit, Renderer2, signal, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { isDefined } from '@libs/utils';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from "primeng/drawer";
import { filter, Subscription } from 'rxjs';
import { AboutMeWalkthroughComponent } from "../../components/about-me-walkthrough/about-me-walkthrough.component";
import { AboutMeService } from '../../services/about-me.service';
import { LayoutService } from '../../services/layout.service';
import { AppTopbar } from './app.topbar';
import { AppSidebar } from './sidebar/sidebar.component';

@Component({
    selector: 'app-layout',
    standalone: true,
    imports: [CommonModule, AppTopbar, AppSidebar, RouterModule, DialogModule, AboutMeWalkthroughComponent, DrawerModule],
    template: `
    <div class="layout-wrapper" [ngClass]="containerClass">
        <!-- TODO: add section for contents -->
        <div class="layout-main-container">
            <app-sidebar style="width: auto;"></app-sidebar>
            <div class="layout-main">
                <app-topbar></app-topbar>
                <router-outlet></router-outlet>
            </div>
        </div>
        <div class="layout-mask animate-fadein"></div>
    </div> 
    <p-drawer header="Cookie Policy" [visible]="visibleCookie" position="bottom" (onHide)="acceptCookie()" [style]="{zIndex: 10000}">
        <p>
            Questo portale utilizza solo cookie tecnici per poterti offrire un corretto funzionamento del sito e un'agevole fruizione dei contenuti e dei servizi dello stesso. 
            <a [href]="cookieLink" target="_blank" class="underline">Leggi l'informativa Cookie</a>
        </p>
    </p-drawer>
    <p-dialog
        [showHeader]="false"
        [visible]="showAboutMeDialog()"
        [modal]="true"
        [closable]="false"
        dismissableMask="true"
        class="about-me-dialog"
        baseZIndex="100"
    >
        <app-about-me-walkthrough [initialStep]="abountMeInitialStep()" [questions]="aboutMeQuestions()" (close)="closeAboutMeDialog()"/>
    </p-dialog>
    
    `
})
export class AppLayout implements OnInit {
    overlayMenuOpenSubscription: Subscription;

    menuOutsideClickListener: any;

    @ViewChild(AppSidebar) appSidebar!: AppSidebar;

    @ViewChild(AppTopbar) appTopBar!: AppTopbar;

    showAboutMeDialog = signal(false);
    aboutMeQuestions = signal<any[]>([]);
    abountMeInitialStep = signal<number>(-1);

    visibleCookie: boolean = false;
    cookieLink: string = window.location.origin + '/policy/cookie-policy.pdf';

    constructor(
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router,
        private aboutMeService: AboutMeService
    ) {
        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
                    if (this.isOutsideClicked(event)) {
                        this.hideMenu();
                    }
                });
            }

            if (this.layoutService.layoutState().staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.hideMenu();
        });
    }

    ngOnInit(): void {
        const showCookie = localStorage.getItem('hide-cookie-policy');
        if (!isDefined(showCookie) || showCookie !== 'true') {
            this.visibleCookie = true;
        } else {
            this.getAboutMe();
        }
    }

    acceptCookie() {
        this.visibleCookie = false;
        localStorage.setItem('hide-cookie-policy', 'true');
        this.getAboutMe();
    }


    closeAboutMeDialog() {
        this.showAboutMeDialog.set(false);
    }

    getAboutMe() {
        this.aboutMeService.getAllAnswers(true).subscribe({
            next: (response) => {
                console.log('About Me answers fetched successfully:', response);
                const questions = response;
                const showAboutMeDialogIndex = questions.findIndex((question) => !isDefined(question.answer));
                if (showAboutMeDialogIndex >= 0) {
                    this.aboutMeQuestions.set(questions);
                    this.showAboutMeDialog.set(true);
                    this.abountMeInitialStep.set(showAboutMeDialogIndex);
                }
            },
            error: (error) => {
                console.error('Error fetching about me answers:', error);
                // Handle error appropriately, e.g., show a notification or log it
            }
        });
    }

    isOutsideClicked(event: MouseEvent) {
        const sidebarEl = document.querySelector('.layout-sidebar');
        const topbarEl = document.querySelector('.layout-menu-button');
        const eventTarget = event.target as Node;

        return !(sidebarEl?.isSameNode(eventTarget) || sidebarEl?.contains(eventTarget) || topbarEl?.isSameNode(eventTarget) || topbarEl?.contains(eventTarget));
    }

    hideMenu() {
        this.layoutService.layoutState.update((prev) => ({ ...prev, overlayMenuActive: false, staticMenuMobileActive: false, menuHoverActive: false }));
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }
        this.unblockBodyScroll();
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    get containerClass() {
        return {
            'layout-overlay': this.layoutService.layoutConfig().menuMode === 'overlay',
            'layout-static': this.layoutService.layoutConfig().menuMode === 'static',
            'layout-static-inactive': this.layoutService.layoutState().staticMenuDesktopInactive && this.layoutService.layoutConfig().menuMode === 'static',
            'layout-overlay-active': this.layoutService.layoutState().overlayMenuActive,
            'layout-mobile-active': this.layoutService.layoutState().staticMenuMobileActive
        };
    }

    ngOnDestroy() {
        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }
    }

    createAnswer(isEditing: boolean) {
        if (isEditing) {
            this.aboutMeService.editAnswer(this.aboutMeQuestions()[this.abountMeInitialStep()], this.aboutMeQuestions()[this.abountMeInitialStep()].id).subscribe({
                next: (response) => {
                    console.log('Answer edited successfully:', response);
                    // this.getAboutMe();
                },
                error: (error) => {
                    console.error('Error editing answer:', error);
                    // Handle error appropriately, e.g., show a notification or log it
                }
            });
        } else {
            this.aboutMeService.createAnswer(this.aboutMeQuestions()[this.abountMeInitialStep()]).subscribe({
                next: (response) => {
                    console.log('Answer created successfully:', response);
                    // this.getAboutMe();
                },
                error: (error) => {
                    console.error('Error creating answer:', error);
                    // Handle error appropriately, e.g., show a notification or log it
                }
            });
        }

    }
}
